import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

/**
 * Run a GSAP "scene" scoped to a container ref, with automatic cleanup.
 *
 *   const ref = useGsapScene((ctx, q) => {
 *     gsap.to(q(".thing"), { ... scrollTrigger: { trigger: el, ... } });
 *   });
 *
 * - `ctx` is the gsap.context (call ctx.add for extra work if needed).
 * - `q` is a scoped selector: q(".sel") → elements inside the container.
 * - Everything created inside is reverted on unmount (kills ScrollTriggers,
 *   tweens, and inline styles GSAP set) via ctx.revert().
 * - Skips entirely under prefers-reduced-motion.
 *
 * `deps` re-runs the scene (e.g. when content that changes layout loads).
 */
export function useGsapScene(build, deps = []) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const reduce =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return undefined;

    const ctx = gsap.context((self) => {
      const q = self.selector;
      build(self, q, el);
    }, el);

    // ScrollTriggers created this frame need bounds recalculated once the
    // DOM/layout has settled (fonts, images, lazy sections).
    const t = setTimeout(() => { try { ScrollTrigger.refresh(); } catch (_) {} }, 60);

    return () => {
      clearTimeout(t);
      ctx.revert();
    };
    // eslint-disable-next-line
  }, deps);

  return ref;
}
